// See EnrollmentVerification.spec.md for ENV-FR-001 through ENV-FR-007
// Step 2 table: one row per host@backend or per returned check, with Retry check on rows that
// need attention (ENV-FR-003, ENV-FR-004).
import { makeStyles } from '@material-ui/styles'
import type { FC } from 'react'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Button from 'core/elements/button'
import Grid from 'core/elements/grid/Grid'
import type { GridCellProps } from 'core/elements/grid/hooks/useGridRows'
import Text from 'core/elements/Text'
import type Theme from 'core/themes/model'
import type { StepTwoRow, StepTwoRowStatus } from './enrollmentSelectors'

const STATUS_CONFIG: Record<StepTwoRowStatus, { label: string; icon: string; spin?: boolean }> = {
  queued: { label: 'Queued', icon: 'circle' },
  checking: { label: 'Checking', icon: 'arrows-rotate', spin: true },
  skipped: { label: 'Skipped', icon: 'minus' },
  passed: { label: 'Completed', icon: 'check' },
  warning: { label: 'Warning', icon: 'triangle-exclamation' },
  failed: { label: 'Failed', icon: 'xmark' },
  notSupported: { label: 'Not supported', icon: 'ban' },
  couldntRun: { label: "Couldn't run", icon: 'circle-question' },
}

// Rows that get a "Recommended action" expand and a Retry check button.
const RETRYABLE_STATUSES: StepTwoRowStatus[] = ['warning', 'failed', 'couldntRun']

type Cell = FC<GridCellProps<StepTwoRow>>

const StatusCell: Cell = ({ item }) => {
  const classes = useStyles()
  const { label, icon, spin } = STATUS_CONFIG[item.status]
  return (
    <span className={classes.statusCell}>
      <span role="img" aria-label={label} title={label}>
        <FontAwesomeIcon solid spin={spin} className={classes[`status_${item.status}`]}>
          {icon}
        </FontAwesomeIcon>
      </span>
      <Text variant="body2">{label}</Text>
    </span>
  )
}

const HostCell: Cell = ({ item }) => <>{item.hostName}</>
const BackendCell: Cell = ({ item }) => <>{item.backend}</>
const CheckCell: Cell = ({ item }) => <>{item.check}</>

// ENV-FR-004: "Recommended action:" plus Retry check, disabled while that host@backend is
// still queued or checking.
const RowDetails = ({
  row,
  retryDisabled,
  onRetry,
}: {
  row: StepTwoRow
  retryDisabled: boolean
  onRetry: (serviceHost: string) => void
}) => {
  const classes = useStyles()
  return (
    <div className={classes.expanded}>
      <Text variant="body2">
        <span className={classes.actionLabel}>Recommended action:</span> {row.detail}
      </Text>
      {row.retryable && (
        <Button
          variant="secondary"
          size="small"
          icon="arrows-rotate"
          disabled={retryDisabled}
          onClick={() => onRetry(row.serviceHost)}
        >
          Retry check
        </Button>
      )}
    </div>
  )
}

export default function StepTwoTable({
  rows,
  onRetry,
}: {
  rows: StepTwoRow[]
  onRetry: (serviceHost: string) => void
}) {
  // A host@backend with a fresh check already queued or running can't be retried again.
  const pending = rows.filter((row) => row.status === 'queued' || row.status === 'checking')
  const pendingServiceHosts = new Set(pending.map((row) => row.serviceHost))
  return (
    <Grid<StepTwoRow>
      uniqueIdentifier="key"
      data={rows}
      columns={[
        { key: 'status', label: 'Status', CellComponent: StatusCell },
        { key: 'hostName', label: 'Host', CellComponent: HostCell },
        { key: 'backend', label: 'Backend', CellComponent: BackendCell },
        { key: 'check', label: 'Check', CellComponent: CheckCell },
      ]}
      label="Storage verification"
      disableToolbar
      disableSorting
      hideCheckboxes
      useFloatingActionBar={false}
      renderExpandedRow={(row) =>
        RETRYABLE_STATUSES.includes(row.status) ? (
          <RowDetails
            row={row}
            retryDisabled={pendingServiceHosts.has(row.serviceHost)}
            onRetry={onRetry}
          />
        ) : null
      }
    />
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  statusCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  status_queued: { color: theme.palette.grey[500] },
  status_checking: { color: theme.palette.blue[700] },
  status_skipped: { color: theme.palette.grey[500] },
  status_passed: { color: theme.palette.green[700] },
  status_warning: { color: theme.palette.yellow[900] },
  status_failed: { color: theme.palette.red[700] },
  status_notSupported: { color: theme.palette.grey[500] },
  status_couldntRun: { color: theme.palette.grey[500] },
  expanded: {
    display: 'grid',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    // Keep Retry check at its own width instead of stretching across the row.
    justifyItems: 'start',
  },
  actionLabel: {
    fontWeight: 600,
  },
}))
